/**
 * Client service for Construction Issues & Approvals.
 * Hindrances, RFIs, Selections, Change Requests, versioned decisions, outside approvals.
 * R39 B5.a + B5.b (plan docs/plans/sector-operations.md row B5 :65).
 * Wraps callables via callable(); components never import firebase/*.
 */

import { callable } from './callable'
import type {
  ConIssue,
  ConIssueEdit,
  ConIssueResponse,
  ConIssueDecision,
  ConIssueState,
  CreateConIssueInput,
  UpdateConIssueInput,
  RespondConIssueInput,
  WithdrawConIssueInput,
  CloseConIssueInput,
  DecideConIssueInput,
  SendConIssueForApprovalInput,
  GetConIssueInput,
  ListConIssuesInput,
  ListConIssueDecisionsInput,
  AttachConIssuePhotosInput,
  GetConIssuePhotoUrlsInput,
  GetConIssuePhotoUrlsResult,
} from '../types/ops'

export async function createConIssue(
  input: CreateConIssueInput,
): Promise<{ issueId: string; registerNo: string; requestId: string; issue: ConIssue; replayed?: boolean }> {
  return callable<
    CreateConIssueInput,
    { issueId: string; registerNo: string; requestId: string; issue: ConIssue; replayed?: boolean }
  >('createConIssue', input)
}

export async function updateConIssue(
  input: UpdateConIssueInput,
): Promise<{ issueId: string; issue: ConIssue; replayed?: boolean }> {
  return callable<UpdateConIssueInput, { issueId: string; issue: ConIssue; replayed?: boolean }>(
    'updateConIssue',
    input,
  )
}

export async function respondConIssue(
  input: RespondConIssueInput,
): Promise<{ issueId: string; responseIndex: number; issue: ConIssue; replayed?: boolean }> {
  return callable<
    RespondConIssueInput,
    { issueId: string; responseIndex: number; issue: ConIssue; replayed?: boolean }
  >('respondConIssue', input)
}

export async function withdrawConIssue(
  input: WithdrawConIssueInput,
): Promise<{
  issueId: string
  state: ConIssueState
  withdrawnReason: string
  requestClosed?: boolean
  requestCloseError?: { code: string; at: number }
  replayed?: boolean
}> {
  return callable<
    WithdrawConIssueInput,
    {
      issueId: string
      state: ConIssueState
      withdrawnReason: string
      requestClosed?: boolean
      requestCloseError?: { code: string; at: number }
      replayed?: boolean
    }
  >('withdrawConIssue', input)
}

export async function closeConIssue(
  input: CloseConIssueInput,
): Promise<{
  issueId: string
  state: ConIssueState
  requestClosed?: boolean
  requestCloseError?: { code: string; at: number }
  replayed?: boolean
}> {
  return callable<
    CloseConIssueInput,
    {
      issueId: string
      state: ConIssueState
      requestClosed?: boolean
      requestCloseError?: { code: string; at: number }
      replayed?: boolean
    }
  >('closeConIssue', input)
}

export async function decideConIssue(
  input: DecideConIssueInput,
): Promise<{
  issueId: string
  version: number
  decision: ConIssueDecision
  issue: ConIssue
  replayed?: boolean
}> {
  return callable<
    DecideConIssueInput,
    {
      issueId: string
      version: number
      decision: ConIssueDecision
      issue: ConIssue
      replayed?: boolean
    }
  >('decideConIssue', input)
}

export async function sendConIssueForApproval(
  input: SendConIssueForApprovalInput,
): Promise<{
  approvalId: string
  approvalState: string
  links: Array<{ approverId: string; phoneMasked: string; token: string }>
  replayed?: boolean
}> {
  return callable<
    SendConIssueForApprovalInput,
    {
      approvalId: string
      approvalState: string
      links: Array<{ approverId: string; phoneMasked: string; token: string }>
      replayed?: boolean
    }
  >('sendConIssueForApproval', input)
}

export async function getConIssue(
  input: GetConIssueInput,
): Promise<{
  issue: ConIssue
  responses: ConIssueResponse[]
  decisions: ConIssueDecision[]
  edits: ConIssueEdit[]
}> {
  return callable<
    GetConIssueInput,
    {
      issue: ConIssue
      responses: ConIssueResponse[]
      decisions: ConIssueDecision[]
      edits: ConIssueEdit[]
    }
  >('getConIssue', input)
}

export async function listConIssues(
  input: ListConIssuesInput,
): Promise<{ issues: ConIssue[] }> {
  return callable<ListConIssuesInput, { issues: ConIssue[] }>('listConIssues', input)
}

export async function listConIssueDecisions(
  input: ListConIssueDecisionsInput,
): Promise<{ decisions: ConIssueDecision[] }> {
  return callable<ListConIssueDecisionsInput, { decisions: ConIssueDecision[] }>(
    'listConIssueDecisions',
    input,
  )
}

export async function attachConIssuePhotos(
  input: AttachConIssuePhotosInput,
): Promise<{ issueId: string; photoPaths: string[]; issue: ConIssue; replayed?: boolean }> {
  return callable<
    AttachConIssuePhotosInput,
    { issueId: string; photoPaths: string[]; issue: ConIssue; replayed?: boolean }
  >('attachConIssuePhotos', input)
}

export async function getConIssuePhotoUrls(
  input: GetConIssuePhotoUrlsInput,
): Promise<GetConIssuePhotoUrlsResult> {
  return callable<GetConIssuePhotoUrlsInput, GetConIssuePhotoUrlsResult>(
    'getConIssuePhotoUrls',
    input,
  )
}
